import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/redux/store";
import { adminAPI } from "@/lib/services/api.service";

export interface TeamMemberStats {
  totalCalls: number;
  completedCalls: number;
  averageCallDuration: number;
  meetingsBooked: number;
}

interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: "SUPER_ADMIN" | "ADMIN" | "SALES_REP";
  role_label?: string | null;
  company_id?: string;
  is_verified: boolean;
  is_online: boolean;
  created_at: string;
  last_seen?: string;
  last_active_at?: string;
  profile_picture?: string | null;
  stats?: TeamMemberStats;
}

interface TeamState {
  members: TeamMember[];
  isLoading: boolean;
  error: string | null;
}

const initialState: TeamState = {
  members: [],
  isLoading: false,
  error: null,
};

export const fetchTeamMembers = createAsyncThunk(
  "team/fetchMembers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminAPI.getUsers();
      return response.users;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch team members");
    }
  }
);

const teamSlice = createSlice({
  name: "team",
  initialState,
  reducers: {
    setTeamMembers: (state, action: PayloadAction<TeamMember[]>) => {
      state.members = action.payload;
    },
    addTeamMember: (state, action: PayloadAction<TeamMember>) => {
      state.members.push(action.payload);
    },
    updateTeamMember: (state, action: PayloadAction<Partial<TeamMember> & { id: string }>) => {
      const index = state.members.findIndex((m) => m.id === action.payload.id);
      if (index !== -1) {
        state.members[index] = { ...state.members[index], ...action.payload };
      }
    },
    removeTeamMember: (state, action: PayloadAction<string>) => {
      state.members = state.members.filter((m) => m.id !== action.payload);
    },
    updateMemberStatus: (
      state,
      action: PayloadAction<{ userId: string; status: "online" | "offline" | "busy" }>
    ) => {
      const member = state.members.find((m) => m.id === action.payload.userId);
      if (member) {
        member.is_online = action.payload.status === "online" || action.payload.status === "busy";
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamMembers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.members = action.payload;
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setTeamMembers,
  addTeamMember,
  updateTeamMember,
  removeTeamMember,
  updateMemberStatus,
  setLoading,
  setError,
} = teamSlice.actions;

export const selectTeamMembers = (state: RootState) => state.team.members;
export const selectTeamLoading = (state: RootState) => state.team.isLoading;
export const selectTeamError = (state: RootState) => state.team.error;

export default teamSlice.reducer;
